import axios, { AxiosError, AxiosResponse } from 'axios';
import { getLogger } from '../diagnostics/logger';
import queryString from 'query-string';
import { getSimManagementErrorFromAxiosError } from '.';
import { DropdownItem } from '../components/ui/input/DropdownInput';
import { MatchingResult } from '../models/matchingResult';
import { Provisioning } from '../models/provision';
import { QueryResult } from '../models/queryResult';
import { BundleSizeInput } from '../models/transfer/input/BundleSizeInput';

import config from '../config';
const log = getLogger('simManagement.api.simProvisionApi');

export function getAvailableTels(accountId: number, networkId: number): Promise<string[]> {
  const url = `${config.API_BASE_URL}api/account/${accountId}/network/${networkId}/availableTels`;
  return new Promise<string[]>((resolve, reject) => {
    axios
      .get(url)
      .then((response: AxiosResponse<string[]>) => {
        resolve(response.data);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to load available telephone numbers.', simManagementError);
        reject(simManagementError);
      });
  });
}

export function getBundleTopups(networkId: number): Promise<DropdownItem[]> {
  const url = `${config.API_BASE_URL}api/network/${networkId}/bundleTopup`;
  return new Promise<DropdownItem[]>((resolve, reject) => {
    axios
      .get(url)
      .then((response: AxiosResponse<BundleSizeInput[]>) => {
        const dropDownItems: DropdownItem[] = response.data.map((b: any) => {
          return {
            key: b.size,
            label: b.name,
          };
        });

        resolve(dropDownItems);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to load bundle topup.', simManagementError);
        reject(simManagementError);
      });
  });
}

export function getExcludedProvisioningDays(commandDefinitionIds: number[]) {
  const url = `${config.API_BASE_URL}api/provision/excludeDays`;
  return new Promise<string[]>((resolve, reject) => {
    axios
      .get(url, {
        params: {
          commandDefinitionIds,
        },
      })
      .then((response: AxiosResponse<string[]>) => {
        resolve(response.data);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to load excluded provisioning days.', simManagementError);
        reject(simManagementError);
      });
  });
}

export function getProvision(tel: string): Promise<Provisioning> {
  const url = `${config.API_BASE_URL}api/provisioning/${tel}`;
  return new Promise<Provisioning>((resolve, reject) => {
    axios
      .get(url)
      .then((response: AxiosResponse<Provisioning>) => {
        response.data.standardBoltOns.forEach((b: any) => b.isExisting = true);
        response.data.temporalBoltOns.forEach((b: any) => b.isExisting = true);
        resolve(response.data);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to load provision data.', simManagementError);
        reject(simManagementError);
      });
  });
}

export function getProvisioningOrder(
  accountId: number,
  networkId: number,
  provisioningNumber: number,
): Promise<ProvisioningOrder> {
  const url = `${config.API_BASE_URL}api/account/${accountId}/network/${networkId}/provisioningOrder/${provisioningNumber}`;
  return new Promise<ProvisioningOrder>((resolve, reject) => {
    axios
      .get(url)
      .then((response: AxiosResponse<ProvisioningOrder>) => {
        resolve(response.data);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to load provisioning orders.', simManagementError);
        reject(simManagementError);
      });
  });
}

export function getProvisioningOrders(accountId: number, networkId: number): Promise<ProvisioningOrder[] | null> {
  const url = `${config.API_BASE_URL}api/account/${accountId}/network/${networkId}/provisioningOrder`;
  return new Promise<ProvisioningOrder[] | null>((resolve, reject) => {
    axios
      .get(url, {
        validateStatus: (status) => (status >= 200 && status < 300) || status === 409,
      })
      .then((response: AxiosResponse<ProvisioningOrder[]>) => {
        if (response.status === 409) {
          resolve(null);
          return;
        }

        resolve(response.data);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to load provisioning orders.', simManagementError);
        reject(simManagementError);
      });
  });
}

export function getAccountReferences(
  companyId: number,
  accountId: number,
  accountReference: string,
): Promise<QueryResult<MatchingResult>> {
  const queryOptions = {
    skip: 0,
    take: 20,
    MatchingValueContains: accountReference,
    wrapWith: '',
  };

  const wrapWithItems = ['count', 'total-count'];

  queryOptions.wrapWith = wrapWithItems.join(',');

  const queryParams = queryString.stringify(queryOptions);
  const url = `${config.API_BASE_URL}api/company/${companyId}/account/${accountId}/sim/getaccountreferences?${queryParams}`;

  return new Promise<QueryResult<MatchingResult>>((resolve, reject) => {
    axios
      .get(url)
      .then((response: AxiosResponse<QueryResult<MatchingResult>>) => {
        resolve(response.data);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to load account references.', simManagementError);
        reject(simManagementError);
      });
  });
}
